import React, { useState, useEffect } from 'react';
import Decimal from 'decimal.js';
import BaseWrapper from 'components/layout/BaseWrapper';
import { ProgressBar } from 'components/WalletEntry';
import { useTournament } from 'context/TournamentContext';
import { usePriceConversion } from 'hooks/usePriceConversion';
import {
  GradientSection,
  HeroImage,
  Headline,
  Subtitle,
  TournamentTitle,
  TournamentTitleLabel,
  CountdownBar,
  CountdownBarLabel,
  CountdownBarValue,
  CountdownSpinner,
  DetailsTable,
  DetailRow,
  DetailLabel,
  DetailValue,
} from './styled';

const getCountdownMs = (targetDate: Date): number => {
  return Math.max(0, targetDate.getTime() - Date.now());
};

// TB-RULE-DECIMAL-BACKEND: monetary conversions go through Decimal.js, never
// raw float multiplication — `amount * pricePerToken` leaks IEEE-754 noise
// onto the screen (issue #480; see TournamentsUser.tsx / TournamentDetailCard).
// `amount` is taken straight from the backend attribute (string | number) so
// the Decimal is built from source, before any `Number()` coercion.
export const usdHint = (amount: string | number, pricePerToken: number): string =>
  new Decimal(String(amount)).mul(new Decimal(String(pricePerToken))).toFixed(2);

export const formatCountdown = (diffMs: number): string => {
  if (diffMs <= 0) return '00s';

  const totalSeconds = Math.ceil(diffMs / 1000);
  const pad = (n: number): string => n.toString().padStart(2, '0');

  // Final stretch: show seconds only, 2-digit padded (60, 30, 05, 01).
  if (totalSeconds <= 60) {
    return `${pad(totalSeconds)}s`;
  }

  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds / (60 * 60)) % 24);
  const minutes = Math.floor((totalSeconds / 60) % 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${pad(hours)}h`);
  parts.push(`${pad(minutes)}m`);
  parts.push(`${pad(seconds)}s`);

  return parts.join(' ');
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = date.getUTCFullYear();
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  return `${day}/${month}/${year}\n${hours}:${minutes} (UTC)`;
};

const formatDuration = (startStr: string, endStr: string): string => {
  const start = new Date(startStr);
  const end = new Date(endStr);
  const diff = end.getTime() - start.getTime();

  if (diff <= 0) return '—';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.join(' ') || '< 1m';
};

const TournamentWaiting: React.FC = () => {
  const { tournamentDetails } = useTournament();

  const rawData = tournamentDetails?.data as any;
  const attrs = rawData?.attributes ?? rawData?.data ?? rawData;
  const tournamentId = rawData?.id;

  const startAt = attrs?.start_at;
  const endAt = attrs?.end_at;
  const stake = Number(attrs?.stake) || 0;
  const rewardPool = Number(attrs?.reward_pool) || 0;
  const tradeType = attrs?.trade_type || 'Spot';

  // Chain-aware ticker + USD rate. Backend populates stake_currency on
  // tournament detail responses (E2.1 / E4.8). Fall back to legacy
  // reward_pool_currency or POL when the relation is missing (very old
  // tournaments or Free tournaments where stake_currency is absent).
  const stakeCurrency = attrs?.stake_currency ?? null;
  const ticker = stakeCurrency?.short_name ?? attrs?.reward_pool_currency ?? 'POL';
  const chainKey: 'polygon' | 'solana' =
    stakeCurrency?.chain === 'solana' ? 'solana' : 'polygon';

  const { pricePerToken } = usePriceConversion({ chain: chainKey, amount: stake, debounceMs: 0 });

  const [remainingMs, setRemainingMs] = useState(() =>
    startAt ? getCountdownMs(new Date(startAt)) : -1,
  );

  // Tick every second so the seconds column updates live.
  useEffect(() => {
    if (!startAt) return;
    const target = new Date(startAt);
    const update = () => setRemainingMs(getCountdownMs(target));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startAt]);

  const countdown = remainingMs < 0 ? '—' : formatCountdown(remainingMs);
  const isExpired = remainingMs === 0;

  const isPaid = stake > 0;
  const displayName = attrs?.name ?? tournamentId;

  return (
    <BaseWrapper compact>
      <GradientSection>
        {isPaid && <ProgressBar currentStep="game" />}
        <HeroImage />
        <div>
          <Headline>You are ready to start the game!</Headline>
          <Subtitle>This tournament hasn't started yet</Subtitle>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
          <TournamentTitle>
            <TournamentTitleLabel>Tournament:</TournamentTitleLabel>{' '}
            {displayName}
          </TournamentTitle>
          <CountdownBarLabel>{isExpired ? 'starting...' : 'starts in'}</CountdownBarLabel>
          <CountdownBar>
            {isExpired ? <CountdownSpinner /> : <CountdownBarValue>{countdown}</CountdownBarValue>}
          </CountdownBar>
        </div>

        <DetailsTable>
          <DetailRow>
            <DetailLabel>Type:</DetailLabel>
            <DetailValue>
              {String(tradeType).toLowerCase() === 'margin' ? 'Margin trading' : 'Spot trading'}
            </DetailValue>
          </DetailRow>
          {startAt && (
            <DetailRow>
              <DetailLabel>Start:</DetailLabel>
              <DetailValue style={{ whiteSpace: 'pre-line' }}>{formatDate(startAt)}</DetailValue>
            </DetailRow>
          )}
          {endAt && (
            <DetailRow>
              <DetailLabel>Finish:</DetailLabel>
              <DetailValue style={{ whiteSpace: 'pre-line' }}>{formatDate(endAt)}</DetailValue>
            </DetailRow>
          )}
          {startAt && endAt && (
            <DetailRow>
              <DetailLabel>Duration:</DetailLabel>
              <DetailValue>{formatDuration(startAt, endAt)}</DetailValue>
            </DetailRow>
          )}
          <DetailRow>
            <DetailLabel>Market(s):</DetailLabel>
            <DetailValue>Cryptocurrencies</DetailValue>
          </DetailRow>
          {stake > 0 && (
            <DetailRow>
              <DetailLabel>Your stake:</DetailLabel>
              <DetailValue>
                {stake} {ticker}
                {pricePerToken != null && ` ≈${usdHint(attrs?.stake ?? 0, pricePerToken)} USD`}
              </DetailValue>
            </DetailRow>
          )}
          {rewardPool > 0 && (
            <DetailRow>
              <DetailLabel>Reward pool:</DetailLabel>
              <DetailValue>
                {rewardPool} {ticker}
                {pricePerToken != null && ` ≈${usdHint(attrs?.reward_pool ?? 0, pricePerToken)} USD`}
              </DetailValue>
            </DetailRow>
          )}
        </DetailsTable>
      </GradientSection>
    </BaseWrapper>
  );
};

export default TournamentWaiting;
